import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheckIcon, ArrowPathIcon, CheckIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usuariosApi } from '../../api/usuarios';
import { PERMISSOES, PERMISSOES_GRUPOS, PERFIL_TEMPLATES } from '../../types/usuarios';
import { useToast } from '../ui/Toast';
import { btnPrimario, btnSecundario } from '../ui/iris';
import { extrairMensagemErro } from '../../utils/extrairMensagemErro';

type TemplateOption = typeof PERFIL_TEMPLATES[number];

interface DiffTemplate {
  template: TemplateOption;
  ganhar: string[];   // não estava, vai estar
  manter: string[];   // estava e vai estar
  remover: string[];  // estava e não vai estar
}

const filtrarDisponiveis = (chaves: readonly string[]): string[] =>
  chaves.filter(c => c in PERMISSOES && PERMISSOES[c as keyof typeof PERMISSOES].status !== 'disabled');

const labelDe = (chave: string) => PERMISSOES[chave as keyof typeof PERMISSOES]?.label ?? chave;

interface Props {
  usuarioId: number;
  perfil?: string;
}

const PermissoesEditor: React.FC<Props> = ({ usuarioId, perfil }) => {
  const toast = useToast();
  const [permissoes, setPermissoes] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState(false);
  // PR5d — diff dialog de substituição. null = fechado. Quando set != null,
  // dialog está aberto pra confirmar troca destrutiva do conjunto atual.
  const [diffSubstituir, setDiffSubstituir] = useState<DiffTemplate | null>(null);

  const isAdmin = perfil === 'ADMIN';

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await usuariosApi.getPermissoesUsuario(usuarioId);
      setPermissoes(data);
    } catch {
      setPermissoes([]);
    } finally {
      setCarregando(false);
    }
  }, [usuarioId]);

  useEffect(() => { carregar(); }, [carregar]);

  const toggle = (chave: string) => {
    setSalvo(false);
    setPermissoes(prev =>
      prev.includes(chave) ? prev.filter(p => p !== chave) : [...prev, chave]
    );
  };

  const marcarTodas = () => {
    setSalvo(false);
    // Marca todas — exceto disabled (não tem sentido ativar feature em dev).
    setPermissoes(
      Object.entries(PERMISSOES)
        .filter(([, meta]) => meta.status !== 'disabled')
        .map(([chave]) => chave)
    );
  };

  const desmarcarTodas = () => {
    setSalvo(false);
    setPermissoes([]);
  };

  // PR5d — operações de template em duas modalidades:
  // - SUBSTITUIR (botão grande): troca o conjunto atual pelo template. Se já tem
  //   permissões marcadas, abre diff dialog pra confirmar (evita inflar set sem
  //   o operador perceber, problema antigo da operação só-union).
  // - ADICIONAR (botão "+"): UNION sem confirm. Útil pra somar perfis tipo
  //   "Comprador + Catálogo" pra usuário multi-papel.
  // Ambas filtram `disabled` defensivamente.
  const calcularDiff = useCallback((template: TemplateOption, atual: string[]): DiffTemplate => {
    const novo = filtrarDisponiveis(template.permissoes);
    const novoSet = new Set(novo);
    const atualSet = new Set(atual);
    return {
      template,
      ganhar: novo.filter(c => !atualSet.has(c)),
      manter: novo.filter(c => atualSet.has(c)),
      remover: atual.filter(c => !novoSet.has(c)),
    };
  }, []);

  const substituirTemplate = (template: TemplateOption) => {
    setSalvo(false);
    if (permissoes.length === 0) {
      setPermissoes(filtrarDisponiveis(template.permissoes));
    } else {
      setDiffSubstituir(calcularDiff(template, permissoes));
    }
  };

  const confirmarSubstituicao = () => {
    if (!diffSubstituir) return;
    setPermissoes(filtrarDisponiveis(diffSubstituir.template.permissoes));
    setDiffSubstituir(null);
  };

  const adicionarTemplate = (template: TemplateOption) => {
    setSalvo(false);
    const disponiveis = filtrarDisponiveis(template.permissoes);
    setPermissoes(prev => Array.from(new Set([...prev, ...disponiveis])));
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await usuariosApi.salvarPermissoes(usuarioId, permissoes);
      setSalvo(true);
      setTimeout(() => setSalvo(false), 3000);
    } catch (err: unknown) {
      // PR2 — auditoria 2026-06-04: tratamento especial pro 403 de escalation.
      // Backend devolve `{ message, permissoesNaoAutorizadas: [...] }` quando USER
      // tenta conceder chave que ele próprio não tem. Mostra a lista pro operador
      // entender o que precisa pedir pro ADMIN.
      const resp = (err as { response?: { data?: { message?: string; permissoesNaoAutorizadas?: string[] } } }).response;
      const naoAutorizadas = resp?.data?.permissoesNaoAutorizadas;
      if (naoAutorizadas && naoAutorizadas.length > 0) {
        const labels = naoAutorizadas.map(c => PERMISSOES[c as keyof typeof PERMISSOES]?.label ?? c);
        toast.erro(`Você não pode conceder: ${labels.join(', ')}. Peça a um administrador.`);
      } else {
        toast.erro(extrairMensagemErro(err, 'Erro ao salvar permissões'));
      }
    } finally {
      setSalvando(false);
    }
  };

  if (isAdmin) {
    return (
      <div className="rounded-iris border border-accent-ring bg-accent-soft p-3">
        <div className="flex items-center gap-2 text-sm text-accent-text">
          <ShieldCheckIcon className="w-4 h-4" />
          Administradores possuem acesso total ao sistema.
        </div>
      </div>
    );
  }

  if (carregando) {
    return (
      <div className="flex items-center gap-2 text-sm text-tx-3 py-3">
        <ArrowPathIcon className="w-4 h-4 animate-spin" /> Carregando permissões...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-tx flex items-center gap-2">
          <ShieldCheckIcon className="w-4 h-4 text-tx-3" />
          Permissões de acesso
        </h3>
        <div className="flex gap-2 text-[11px]">
          <button onClick={marcarTodas} className="text-accent-text hover:underline">Marcar todas</button>
          <span className="text-tx-3">|</span>
          <button onClick={desmarcarTodas} className="text-tx-2 hover:underline">Desmarcar</button>
        </div>
      </div>

      {/* Templates de perfil — cada template tem 2 ações:
          • clicar no nome do template → SUBSTITUI conjunto atual (com diff dialog se não-vazio)
          • clicar no "+" lateral → ADICIONA template (UNION sem confirm)
          Permite tanto trocar de papel (operador → comprador) quanto compor
          papéis (Comprador + Catálogo) sem inflar silenciosamente. */}
      <div className="rounded-iris border border-accent-ring bg-accent-soft p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <SparklesIcon className="w-4 h-4 text-accent-text" />
          <span className="text-[11px] font-semibold text-accent-text uppercase tracking-wide">
            Classificar pelo modelo de perfil
          </span>
          <span className="text-[11px] text-tx-3">
            (clique no nome pra substituir, no <span className="font-bold">+</span> pra adicionar)
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERFIL_TEMPLATES.map(template => (
            <div
              key={template.nome}
              className="inline-flex rounded-iris-sm border border-line-strong overflow-hidden bg-surface"
            >
              <button
                type="button"
                onClick={() => substituirTemplate(template)}
                title={`Substituir permissões atuais por "${template.nome}". ${template.descricao}`}
                className="px-2.5 py-1 text-xs font-medium text-accent-text hover:bg-surface-2 transition-colors"
              >
                {template.nome}
                <span className="ml-1 text-[10px] text-tx-3">({template.permissoes.length})</span>
              </button>
              <button
                type="button"
                onClick={() => adicionarTemplate(template)}
                title={`Adicionar permissões de "${template.nome}" às já marcadas (sem remover nenhuma).`}
                aria-label={`Adicionar ${template.nome} ao set atual`}
                className="px-2 py-1 text-xs font-bold text-accent-text bg-surface-2 hover:bg-surface border-l border-line transition-colors"
              >
                +
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {PERMISSOES_GRUPOS.map((grupo) => {
          // Filtra disabled features — não aparecem como toggle no editor
          // (defesa em camada: nem ADMIN consegue liberar tela em dev).
          const chavesGrupo = grupo.chaves.filter(
            c => c in PERMISSOES && PERMISSOES[c].status !== 'disabled'
          );
          if (chavesGrupo.length === 0) return null;
          const todasAtivas = chavesGrupo.every(c => permissoes.includes(c));
          const algumaAtiva = chavesGrupo.some(c => permissoes.includes(c));

          const toggleGrupo = () => {
            setSalvo(false);
            if (todasAtivas) {
              setPermissoes(prev => prev.filter(p => !chavesGrupo.includes(p as never)));
            } else {
              setPermissoes(prev => Array.from(new Set([...prev, ...chavesGrupo])));
            }
          };

          return (
            <div key={grupo.label} className="rounded-iris border border-line overflow-hidden">
              <div className="flex items-center justify-between border-b border-line bg-surface-2 px-3 py-2">
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-tx-2">
                  {grupo.label}
                  <span className="ml-2 text-tx-3 font-normal normal-case">
                    ({chavesGrupo.filter(c => permissoes.includes(c)).length}/{chavesGrupo.length})
                  </span>
                </h4>
                <button onClick={toggleGrupo} className="text-[11px] text-accent-text hover:underline">
                  {todasAtivas ? 'Desmarcar todas' : algumaAtiva ? 'Marcar restantes' : 'Marcar todas'}
                </button>
              </div>
              <div className="p-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {chavesGrupo.map((chave) => {
                  const ativo = permissoes.includes(chave);
                  const label = PERMISSOES[chave].label;
                  const beta = PERMISSOES[chave].status === 'beta';
                  return (
                    <label
                      key={chave}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-iris-sm cursor-pointer transition-colors text-sm select-none ${
                        ativo ? 'bg-accent-soft text-accent-text' : 'text-tx-2 hover:bg-surface-2'
                      }`}
                    >
                      {/* Toggle visual (switch) ao invés de checkbox padrão */}
                      <span
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          ativo ? 'bg-accent' : 'bg-surface-2 border-line'
                        }`}
                        onClick={(e) => { e.preventDefault(); toggle(chave); }}
                      >
                        <span
                          aria-hidden="true"
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            ativo ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </span>
                      <input
                        type="checkbox"
                        checked={ativo}
                        onChange={() => toggle(chave)}
                        className="sr-only"
                      />
                      <span className="flex-1">
                        {label}
                        {beta && (
                          <span className="ml-2 text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-300 bg-amber-400/15 px-1.5 py-0.5 rounded-iris-sm">
                            Beta
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <button onClick={salvar} disabled={salvando} className={`${btnPrimario} h-8 px-3 text-xs`}>
          {salvando ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <CheckIcon className="w-3.5 h-3.5" />}
          {salvando ? 'Salvando...' : 'Salvar permissões'}
        </button>
        {salvo && <span className="text-xs text-ok">Permissões salvas!</span>}
      </div>

      {/* PR5d — diff dialog de substituição. z-[60] pra ficar acima do
          dialog pai (z-50). Lista ganhar/perder/manter pra que o operador
          entenda exatamente o impacto antes de confirmar. */}
      {diffSubstituir && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="diff-substituir-titulo"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-[var(--scrim)] p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDiffSubstituir(null); }}
        >
          <div className="w-full max-w-md rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-line px-4 py-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-accent-text" aria-hidden />
                <h2 id="diff-substituir-titulo" className="text-sm font-semibold text-tx">
                  Substituir por "{diffSubstituir.template.nome}"?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setDiffSubstituir(null)}
                className="text-tx-3 hover:text-tx"
                aria-label="Fechar"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3 text-sm">
              <p className="text-xs text-tx-2">
                Atual: <span className="font-semibold">{permissoes.length}</span> permissão(ões).
                {' '}Modelo: <span className="font-semibold">{filtrarDisponiveis(diffSubstituir.template.permissoes).length}</span>.
                {' '}Após substituir: <span className="font-semibold">{diffSubstituir.ganhar.length + diffSubstituir.manter.length}</span>.
              </p>
              {diffSubstituir.ganhar.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wide text-ok mb-1">
                    Vai ganhar ({diffSubstituir.ganhar.length})
                  </p>
                  <ul className="space-y-0.5 text-xs text-tx pl-1">
                    {diffSubstituir.ganhar.map(c => (
                      <li key={c}><span className="text-ok font-mono">+</span> {labelDe(c)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {diffSubstituir.remover.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wide text-danger mb-1">
                    Vai perder ({diffSubstituir.remover.length})
                  </p>
                  <ul className="space-y-0.5 text-xs text-tx pl-1">
                    {diffSubstituir.remover.map(c => (
                      <li key={c}><span className="text-danger font-mono">−</span> {labelDe(c)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {diffSubstituir.manter.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase font-semibold tracking-wide text-tx-3 mb-1">
                    Mantém ({diffSubstituir.manter.length})
                  </p>
                  <ul className="space-y-0.5 text-xs text-tx-3 pl-1">
                    {diffSubstituir.manter.map(c => (
                      <li key={c}><span className="font-mono">=</span> {labelDe(c)}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-line px-4 py-3 flex-shrink-0">
              <button type="button" onClick={() => setDiffSubstituir(null)} className={btnSecundario}>
                Cancelar
              </button>
              <button type="button" onClick={confirmarSubstituicao} className={btnPrimario}>
                Substituir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermissoesEditor;
